import React, { useCallback, useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Col, Row, Modal, DatePicker, Button, Space, Switch } from "antd";
import { Form, Input, Select } from "formik-antd";
import { Formik } from "formik";
import { useIntl } from "react-intl";
import { isEmpty, pick, forEach } from "lodash";
import { useDispatch, useSelector } from "react-redux";

import { SELECT_CONTRACT_STATUS } from "@iso/constants/select.constant";
import DetailButtonGroup from "@iso/components/StoreAddEditComponent/DetailButtonGroup";
import storeActions from "@iso/redux/storeAddEdit/actions";
import otaActions from "@iso/redux/ota/actions";
import EditButtonGroup from "@iso/components/StoreAddEditComponent/EditButtonGroup";
import StoreOtaList from "@iso/components/StoreAddEditComponent/store-ota-list/StoreOtaList";
import { initOtaData } from "@iso/components/StoreAddEditComponent/store-ota-list/data";
import {
  ROLES,
  X_REQUESTED_STOREID,
  OTA_STATUS,
  DATE_FORMAT,
  searchSelectLabel,
  searchSelect,
} from "@iso/constants/common.constant";
import {
  itemLeftLabel,
  itemRightLabel,
  colRight,
  colLeft,
  colFull,
  itemFullLabel,
} from "@iso/assets/styles/form.style";
import LayoutContent from "@iso/components/utility/layoutContent";
import PageHeader from "@iso/components/utility/pageHeader";

import { LayoutEditStoreWrapper } from "./StoreAddAndEdit.styles";
import validationSchema from "./validation.schema";
import { Helmet } from "react-helmet";
import DisplayUserUpdate from "@iso/components/common/DisplayUserUpdate";
import moment from "moment/moment";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import SelectTeamForm from "@iso/components/StoreAddEditComponent/select-team/SelectTeamForm";
import teamAction from "@iso/redux/team/actions";

export const OtaContext = React.createContext({});

const StoreAddAndEdit = () => {
  const { messages } = useIntl();
  const params = useParams();
  const { RangePicker } = DatePicker;

  const [editorMode, setEditorMode] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const [initialValues, setInitialValues] = useState(initialValueDefault);
  const [isDeleteStore, setIsDeleteStore] = useState(false);
  const {
    StoreAddEdit: { store, chains, directors, otaOptionData, storeAccounts },
  } = useSelector((state) => state);
  const { teamOptions } = useSelector((state) => state.Team);
  const { user, dashboardRoute } = useSelector((state) => state.Auth);
  const role = user.role;

  const isAdmin = role === ROLES.ADMIN || role === ROLES.SUBADMIN;
  const isStore = role === ROLES.STORE;
  const isUser = role === ROLES.USER;

  let storeId = null;
  if (isStore) {
    if (user.storeId !== null && user.storeId !== undefined) {
      storeId = user.storeId
    } else if (sessionStorage.getItem(X_REQUESTED_STOREID) !== null && sessionStorage.getItem(X_REQUESTED_STOREID) !== undefined) {
      storeId = parseInt(sessionStorage.getItem(X_REQUESTED_STOREID))
    }
  } else {
    storeId = params.storeId
  }
  const accountNoHasStore = isStore && !storeId;

  useEffect(() => {
    if (storeId) {
      dispatch(storeActions.getStoreDetail({ storeId }));
      dispatch(storeActions.getAccountByStore({ storeId }));
    }
    dispatch(teamAction.getTeamOptions());
    dispatch(otaActions.getOtaTypes());
    dispatch(otaActions.getOTAs({ size: 0, status: OTA_STATUS.ENABLED }));

  }, [dispatch, storeId]);

  useEffect(() => {
    if (storeId && !isEmpty(store)) {
      dispatch(storeActions.getStoreMetadata({ currentDirector: store.directorId }));
      if (!isEmpty(user) &&
          (store.contractStatus === "解約" || store.contractStatus === "停止") &&
          store.storeId !== user.storeId &&
          user.role === ROLES.ADMIN
      ) {
        setIsDeleteStore(true);
      }
    } else {
      dispatch(storeActions.getStoreMetadata({ currentDirector: null }));
    }
  }, [dispatch, storeId, store, user]);

  useEffect(() => {
    if (storeId && !isEmpty(store)) {
      let otas = {};
      forEach(store.otas, (ota) => {
        if (!isEmpty(otas[ota.otaTypeId])) {
          otas[ota.otaTypeId].push(ota);
        } else {
          otas[ota.otaTypeId] = [ota];
        }
      });
      let serviceContractPeriod = []
      if (store.serviceContractStart !== null && store.serviceContractStart) {
        serviceContractPeriod[0] = moment(store.serviceContractStart)
      } else {
        serviceContractPeriod[0] = null
      }
      if (store.serviceContractEnd !== null && store.serviceContractEnd) {
        serviceContractPeriod[1] = moment(store.serviceContractEnd)
      }  else {
        serviceContractPeriod[1] = null
      }
      setInitialValues((prevState) => ({
        ...prevState,
        ...store,
        otas,
        serviceContractPeriod
      }));
    }
  }, [storeId, store]);

  useEffect(() => {
    if (accountNoHasStore) {
      Modal.error({
        title: messages["page.storeAddEditEmail.modal.error"],
        content: messages["page.storeAddEditEmail.modal.errorAccountNoHasStore"],
        onOk: () => {
          history.push(dashboardRoute);
        },
      });
    }
  }, [accountNoHasStore, messages, history, dashboardRoute]);

  const onSubmit = useCallback(
    (data, form) => {
      const schema = validationSchema(messages);
      const dataTransform = schema.cast(data);
      const otas = [];
      forEach(dataTransform.otas, (value) => {
        otas.push(...value);
      });
      dataTransform.otas = otas;
      dataTransform.serviceContractStart = dataTransform.serviceContractPeriod && dataTransform.serviceContractPeriod[0] ? moment(dataTransform.serviceContractPeriod[0]).format(DATE_FORMAT) : null;
      dataTransform.serviceContractEnd = dataTransform.serviceContractPeriod && dataTransform.serviceContractPeriod[1] ? moment(dataTransform.serviceContractPeriod[1]).format(DATE_FORMAT) : null;
      new Promise((resolve, reject) => {
        if (storeId) {
          const submitData = pick(dataTransform, [
            "contractStatus",
            "directorId",
            "adInCharges",
            "managerMail",
            "managerName",
            "managerPhone",
            "name",
            "otas",
            "siteControllers",
            "chainId",
            "note",
            "link",
            "deliverySheet",
            "serviceContractStart",
            "serviceContractEnd",
            "deliverySheetApi",
            "deliverySheetAdditional",
            "loginSheet",
            "teams",
            "isForceLogin",
          ]);
          return dispatch(
            storeActions.editStore({ storeId, data: submitData, resolve, reject })
          );
        }

        return dispatch(
          storeActions.createStore({ data: dataTransform, resolve, reject })
        );
      })
        .then(() => {
          if (!storeId) {
            Modal.success({
              title: messages["page.storeAddEditEmail.modal.success"],
              content: (
                <div>{messages["page.storeAddEditEmail.modal.addSuccess"]}</div>
              ),
            });
            form.resetForm();
          } else {
            dispatch(storeActions.getStoreDetail({ storeId }));
            setEditorMode(false);
            Modal.success({
              title: messages["page.storeAddEditEmail.modal.success"],
              content: (
                <div>{messages["page.storeAddEditEmail.modal.updateSuccess"]}</div>
              ),
            });
            form.setSubmitting(false);
          }
        })
        .catch(() => {
          const content = storeId ? (
            <div>{messages["page.storeAddEditEmail.modal.errorUpdate"]}</div>
          ) : (
            <div>{messages["page.storeAddEditEmail.modal.errorCreate"]}</div>
          );
          Modal.error({
            title: messages["page.storeAddEditEmail.modal.error"],
            content: content,
          });
          form.setSubmitting(false);
        });
    },
    [dispatch, storeId, messages]
  );

  const handleFormChange = useCallback(
    (setFieldValue) => (name, value) => {
      setFieldValue(name, value);
    },
    []
  );

  const editing = editorMode || !storeId;
  const pageTitle = isStore
    ? messages["sidebar.storeSetting"]
    : storeId
    ? messages["sidebar.editStore"]
    : messages["sidebar.addStore"];

  const deleteStoreConfirm = () => {
    if (storeId && store && storeAccounts && isDeleteStore) {
      const content = [store.name, ...storeAccounts.map(account => `${account?.displayName}${messages["page.storeAddEditEmail.person"]}`).filter(Boolean)].join('\n');
      Modal.confirm({
        title: messages["page.storeAddEditEmail.modal.delete.title"],
        icon: <ExclamationCircleOutlined />,
        content: (
          <div style={{ maxHeight: "300px", overflowY: "auto" }}>
            {content}
          </div>
        ),
        okText: messages["page.btn.delete"],
        cancelText: messages["page.btn.cancel"],
        onOk:handleDeleteStore,
      });
    }
  };

  const handleDeleteStore = useCallback(
    () => {
      new Promise((resolve, reject) => {
        if (storeId) {
          return dispatch(
            storeActions.deleteStore({
              storeId,
              resolve,
              reject
            })
          );
        }
      }).then(() => {
        Modal.success({
          title: messages["page.storeAddEditEmail.modal.DeleteSuccess.title"],
          content: <div>{messages["page.storeAddEditEmail.modal.DeleteSuccess.content"]}</div>,
          onOk: () =>
            history.push(`${dashboardRoute}/stores`),
        });
      }).catch(() => {
        Modal.error({
          title: messages["page.storeAddEditEmail.modal.error"],
          content: <div>{messages["page.storeAddEditEmail.modal.errorDelete"]}</div>,
        });
      })
    },[storeId, dispatch, dashboardRoute, history]
  );

  return (
    <LayoutEditStoreWrapper>
      <Helmet>
        <meta charSet="utf-8" />
        <title>
          {!isStore && storeId ? `[施設${storeId}] ${initialValues.name} | ` : ""}
          {pageTitle} | HLS
        </title>
      </Helmet>
      <PageHeader>{pageTitle}</PageHeader>
      <LayoutContent>
        <Formik
          onSubmit={onSubmit}
          initialValues={initialValues}
          enableReinitialize={true}
          validationSchema={validationSchema(messages, role, storeId)}
          validateOnChange={true}
          validateOnBlur={true}
          render={({ values, isSubmitting, setFieldValue, resetForm, dirty, touched, errors, setFieldTouched }) => (
            <Form colon={false} labelAlign="left" className="hls-form">
              <Row justify="end">
                <Space align="end">
                  {!editing && (
                    <DetailButtonGroup
                      isSubmitting={isSubmitting}
                      editClick={() => setEditorMode(true)}
                      backClick={() =>
                        history.push(
                          isStore ? dashboardRoute : `${dashboardRoute}/stores`
                        )
                      }
                      disabledEdit={accountNoHasStore}
                      isShowDeleteBtn={role === ROLES.ADMIN}
                      disabledDelete={!storeId || !isDeleteStore}
                      deleteStoreConfirm={deleteStoreConfirm}
                    />
                  )}
                  {editing && (
                    <EditButtonGroup
                      isSubmitting={isSubmitting}
                      dirty={dirty}
                      cancelClick={() => {
                        if (storeId) {
                          setEditorMode(false);
                          resetForm();
                        } else {
                          history.push(`${dashboardRoute}/stores`);
                        }
                      }}
                    />
                  )}
                </Space>
              </Row>
              <Row>
                <Col {...colLeft}>
                  <Form.Item
                    required
                    {...itemLeftLabel}
                    label={messages["page.storeAddEditEmail.contractStatus"]}
                    name="contractStatus"
                  >
                    <Select name="contractStatus" disabled={!isAdmin || !editing}  {...searchSelect}>
                      {SELECT_CONTRACT_STATUS.map((select, index) => (
                        <Select.Option key={index} value={select}>
                          {select}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col {...colRight}>
                  <Form.Item
                    required
                    {...itemRightLabel}
                    label={messages["page.storeAddEditEmail.director"]}
                    name="directorId"
                  >
                    <Select name="directorId" disabled={!isAdmin || !editing} {...searchSelect} >
                      {directors.map((item, index) => (
                        <Select.Option key={index} value={item.directorId}>
                          {item.displayName}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col {...colLeft}>
                  <Form.Item
                      {...itemLeftLabel}
                      name="adInCharges"
                      label={messages["page.storeAddEditEmail.adInCharge"]}
                  >
                    <Select name="adInCharges"
                            mode="multiple"
                            allowClear={true}
                            {...searchSelectLabel}
                            disabled={!isAdmin || !editing}
                    >
                      {directors.map((item, index) => (
                          <Select.Option key={index} value={item.directorId} label={item.displayName}>
                            {item.displayName}
                          </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col {...colRight}>
                  <Form.Item
                    {...itemRightLabel}
                    required
                    name="name"
                    label={messages["page.storeAddEditEmail.storeName"]}
                  >
                    <Input name="name" disabled={isUser || !editing} />
                  </Form.Item>
                </Col>
                <Col {...colLeft}>
                  <Form.Item
                    {...itemLeftLabel}
                    required
                    name="managerName"
                    label={messages["page.storeAddEditEmail.managerName"]}
                  >
                    <Input name="managerName" disabled={isUser || !editing} />
                  </Form.Item>
                </Col>
                <Col {...colRight}>
                  <Form.Item
                    {...itemRightLabel}
                    name="chainId"
                    label={messages["page.storeAddEditEmail.chain"]}
                  >
                    <Select name="chainId" disabled={!isAdmin || !editing} {...searchSelect} >
                      {chains.map((select, index) => (
                        <Select.Option key={index} value={select.chainId}>
                          {select.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col {...colLeft}>
                  <Form.Item
                    {...itemLeftLabel}
                    required
                    name="managerMail"
                    label={messages["page.storeAddEditEmail.managerMail"]}
                  >
                    <Input name="managerMail" disabled={isUser || !editing} />
                  </Form.Item>
                </Col>
                <Col {...colRight}>
                  <Form.Item
                    {...itemRightLabel}
                    required
                    label={messages["page.storeAddEditEmail.managerPhone"]}
                    name="managerPhone"
                  >
                    <Input name="managerPhone" disabled={isUser || !editing} />
                  </Form.Item>
                </Col>
                <Col {...colLeft}>
                  <Form.Item
                      {...itemLeftLabel}
                      label={messages["page.storeAddEditEmail.link"]}
                      name="link"
                  >
                    <Input name="link" disabled={isUser || !editing} />
                  </Form.Item>
                </Col>
                <Col {...colRight}>
                  <Form.Item
                      {...itemRightLabel}
                      label={messages["page.storeAddEditEmail.deliverySheet"]}
                      name="deliverySheet"
                  >
                    <Input name="deliverySheet" disabled={isUser || !editing} />
                  </Form.Item>
                </Col>
                <Col {...colLeft}>
                  <Form.Item
                      {...itemLeftLabel}
                      label={messages["page.storeAddEditEmail.serviceContractPeriod"]}
                      name="serviceContractPeriod"
                  >
                    <RangePicker
                        format={DATE_FORMAT}
                        name="serviceContractPeriod"
                        className="rangeDatePicker"
                        placeholder={[messages["page.storeAddEditEmail.serviceContractStart"], messages["page.storeAddEditEmail.serviceContractEnd"]]}
                        allowEmpty={[true, true]}
                        disabled={isUser || !editing}
                        value={[
                          values.serviceContractPeriod && values.serviceContractPeriod[0] ? moment(values.serviceContractPeriod[0]) : null,
                          values.serviceContractPeriod && values.serviceContractPeriod[1] ? moment(values.serviceContractPeriod[1]) : null
                        ]}
                        onChange={(date, dateString) => {setFieldValue('serviceContractPeriod', dateString)}}
                        inputReadOnly={true}
                    />
                  </Form.Item>
                </Col>
                <Col {...colRight}>
                  <Form.Item
                      {...itemRightLabel}
                      label={messages["page.storeAddEditEmail.deliverySheetApi"]}
                      name="deliverySheetApi"
                  >
                    <Input name="deliverySheetApi" disabled={isUser || !editing} />
                  </Form.Item>
                </Col>
                <Col {...colLeft}>
                  <Form.Item
                      {...itemLeftLabel}
                      label={messages["page.storeAddEditEmail.deliverySheetAdditional"]}
                      name="deliverySheetAdditional"
                  >
                    <Input name="deliverySheetAdditional" disabled={isUser || !editing} />
                  </Form.Item>
                </Col>
                <Col {...colRight}>
                  <Form.Item
                      {...itemRightLabel}
                      label={messages["page.storeAddEditEmail.loginSheet"]}
                      name="loginSheet"
                  >
                    <Input name="loginSheet" disabled={isUser || !editing} />
                  </Form.Item>
                </Col>
                {(isAdmin || isUser) && (
                    <SelectTeamForm
                        name={"teams"}
                        options={teamOptions}
                        disabled={isUser || !editing}
                        required={true}
                        title={messages["page.storeAddEditEmail.executedTeam"]}
                        styleField={{
                          column: colLeft,
                          label: itemLeftLabel,
                        }}
                        setFieldValue={setFieldValue}
                        multiple={true}
                        values={values.teams}
                        className="selected-executed-team"
                        touched={touched}
                        errors={errors}
                        setFieldTouched={setFieldTouched}
                    />
                )}
                <Col {...colFull}>
                  <Form.Item
                    {...itemFullLabel}
                    name="remarks"
                    label={messages["page.storeAddEditEmail.remarks"]}
                  >
                    <Input.TextArea
                      rows={5}
                      name="note"
                      disabled={isUser || !editing}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <OtaContext.Provider value={values.otas}>
                    <StoreOtaList
                      otaOptionData={otaOptionData}
                      onFormChange={handleFormChange(setFieldValue)}
                      otas={values.otas}
                      editing={editing}
                    />
                  </OtaContext.Provider>
                </Col>
                {(isAdmin || isUser) && (
                  <Col span={24}>
                    <Form.Item
                      {...colForceLogin}
                      name="isForceLogin"
                      label={messages["page.storeAddEditEmail.isForceLogin"]}
                    >
                      <Switch
                        name="isForceLogin"
                        disabled={isUser || !editing}
                        checked={values.isForceLogin}
                        onChange={(checked) => setFieldValue('isForceLogin', checked)}
                      />
                    </Form.Item>
                  </Col>
                )}
              </Row>
              { storeId != null && (
                  <DisplayUserUpdate updatedAt={values.updatedAt} updatedUserName={values.updatedName}/>
              )}
            </Form>
          )}
        />
      </LayoutContent>
    </LayoutEditStoreWrapper>
  );
};

const initialValueDefault = {
  contractStatus: "",
  directorId: null,
  name: "",
  managerName: "",
  chainId: null,
  managerPhone: "",
  managerMail: "",
  note: "",
  otas: initOtaData,
  updatedAt: "",
  updatedName: "",
  serviceContractPeriod: [null, null],
  teams: [],
  adInCharges: [],
  isForceLogin: false,
};
const colForceLogin = {
  labelCol: { lg: 5, xs: 24 },
  wrapperCol: { lg: 19, xs: 24 },
};
export default StoreAddAndEdit;
